import { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { InfoBanner } from "@/components/InfoBanner";
import { useTutorialContext } from "@/providers/TutorialProvider";
import { tutorials, ui, notifications } from "@/strings";
import { mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useApiClient } from "@/providers/ApiClientProvider";
import { useQueryClient } from "@tanstack/react-query";
import { cleanupTutorialResourcesSafe } from "@/lib/tutorials/cleanup";
import { useNotification } from "@/providers/NotificationProvider";

interface TutorialInvitationBannerProps {
  /** Tutorial ID to check/start */
  tutorialId: string;
  /** Number of steps in the tutorial (for startTutorial call) */
  totalSteps: number;
  /** Whether to show the banner (determined by parent) */
  shouldShow: boolean;
}

/**
 * Tutorial invitation banner that appears above the criteria section
 * on empty assignments to invite users to start the tutorial.
 *
 * Supports two dismissal modes:
 * - "Skip for now" (session only): Hides banner until browser tab closes
 * - "Never show again" (permanent): Saves preference to user account
 */
export function TutorialInvitationBanner({
  tutorialId,
  totalSteps,
  shouldShow,
}: TutorialInvitationBannerProps) {
  const tutorialContext = useTutorialContext();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { show } = useNotification();
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [neverShowAgain, setNeverShowAgain] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState(false);

  const SESSION_STORAGE_KEY = `markeroo-tutorial-invitation-dismissed-${tutorialId}`;

  // Check sessionStorage on mount to see if user dismissed this session
  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (dismissed === "true") {
        setSessionDismissed(true);
      }
    } catch (error) {
      // SessionStorage may fail in private browsing mode
      console.warn("Failed to read from sessionStorage:", error);
    }
  }, [SESSION_STORAGE_KEY]);

  const handleStartTutorial = useCallback(() => {
    // Start the tutorial via TutorialProvider
    tutorialContext.startTutorial(tutorialId, totalSteps);

    // Hide banner immediately (tutorial will take over)
    setSessionDismissed(true);
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
    } catch (error) {
      console.warn("Failed to write to sessionStorage:", error);
    }
  }, [tutorialContext, tutorialId, totalSteps, SESSION_STORAGE_KEY]);

  const handleCloseBanner = useCallback(() => {
    // Open confirmation modal instead of immediate dismissal
    setIsSkipModalOpen(true);
  }, []);

  const handleConfirmSkip = useCallback(async () => {
    if (neverShowAgain) {
      try {
        // Permanently skip via TutorialProvider (saves to user preferences)
        await tutorialContext.skipTutorial(true);

        // Only cleanup if skip succeeded
        await cleanupTutorialResourcesSafe(api, queryClient, {
          invalidateCaches: true,
          clearState: true,
        });
      } catch (error) {
        // skipTutorial failed - don't cleanup, show error to user
        console.error('[TutorialInvitationBanner] Failed to save skip preference:', error);
        show(notifications.generic.saveError, "error");
        return; // Don't close modal - let user retry
      }
    } else {
      // Skip for this session only - no backend call, safe to proceed
      setSessionDismissed(true);
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
      } catch (error) {
        console.warn("Failed to write to sessionStorage:", error);
      }
    }

    // Only close modal and reset if everything succeeded
    setIsSkipModalOpen(false);
    setNeverShowAgain(false);
  }, [neverShowAgain, tutorialContext, api, queryClient, SESSION_STORAGE_KEY, show]);

  const handleCancelSkip = useCallback(() => {
    setIsSkipModalOpen(false);
    setNeverShowAgain(false);
  }, []);

  // Hide on mobile (tutorial system doesn't support mobile)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Don't render if conditions not met or user dismissed
  if (!shouldShow || sessionDismissed || isMobile) {
    return null;
  }

  return (
    <>
      <div
        data-tutorial-target="tutorial-invitation-banner"
        data-testid="tutorial-invitation-banner"
      >
        <InfoBanner
          title={tutorials.invitation.title}
          infoLines={[tutorials.invitation.description]}
          onDismiss={handleCloseBanner}
          action={
            <button
              type="button"
              className={buttonVariants.primary}
              onClick={handleStartTutorial}
              data-testid="tutorial-start-button"
            >
              {tutorials.invitation.startButton}
            </button>
          }
        />
      </div>

      {/* Skip Confirmation Modal */}
      <Modal isOpen={isSkipModalOpen} onClose={handleCancelSkip} size="sm">
        <ModalHeader title={tutorials.invitation.skipTitle} onClose={handleCancelSkip} />
        <ModalBody>
          <p className={mutedText}>{tutorials.invitation.skipMessage}</p>
          <label className="flex items-start gap-3 mt-4 cursor-pointer">
            <input
              type="checkbox"
              checked={neverShowAgain}
              onChange={(e) => setNeverShowAgain(e.target.checked)}
              className="mt-1 w-4 h-4 text-brand-600 border rounded focus:ring-brand-500"
            />
            <span className="text-sm text-theme-secondary">
              {tutorials.invitation.neverShowAgainLabel}
            </span>
          </label>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className={buttonVariants.secondary}
            onClick={handleCancelSkip}
          >
            {ui.actions.cancel}
          </button>
          <button
            type="button"
            className={buttonVariants.primary}
            onClick={() => void handleConfirmSkip()}
          >
            {ui.actions.confirm}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
}
